import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Shield, 
  DollarSign, 
  Truck, 
  Clock, 
  FileCheck, 
  Headphones,
  Users,
  Fuel,
  Settings2,
  MapPin,
  Calendar as CalendarIcon,
  X,
  Check,
  ChevronDown,
  Phone,
  Mail,
  IdCard,
  CreditCard,
  Car,
  CalendarDays,
  CalendarRange,
  FileText,
  Building2,
  UserCheck,
  Plane,
  Globe,
  Instagram,
  Menu,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Vehicle Data
const vehicles = [
  {
    id: 1,
    name: "Citroen C3",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/a1hy4qhp_citroen%20pa%20background%20.png",
    seats: 5,
    transmission: "Manual",
    fuel: "Petrol"
  },
  {
    id: 2,
    name: "Renault Clio",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/hrbhm53y_ranault%20clio%20pa%20background%20.png",
    seats: 5,
    transmission: "Automatic",
    fuel: "Petrol"
  },
  {
    id: 3,
    name: "VW Golf",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/lmasat56_golf%20pa%20background%20.png",
    seats: 5,
    transmission: "Automatic",
    fuel: "Petrol"
  },
  {
    id: 4,
    name: "Fiat 500 X",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/qmim8gjo_fiat%20500%20pa%20background%20.png",
    seats: 5,
    transmission: "Automatic",
    fuel: "Petrol"
  },
  {
    id: 5,
    name: "Kia Stonic",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/z3gat0sc_kia%20stonic%20pa%20background.png",
    seats: 5,
    transmission: "Automatic",
    fuel: "Petrol"
  },
  {
    id: 6,
    name: "VW Jetta",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/hay56d8e_jetta%20pa%20background%20.png",
    seats: 5,
    transmission: "Automatic",
    fuel: "Petrol"
  },
  {
    id: 7,
    name: "Skoda Octavia",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/ryv0979l_octavia%20pa%20background%20.png",
    seats: 5,
    transmission: "Automatic",
    fuel: "Petrol"
  },
  {
    id: 8,
    name: "SsangYong Korando",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/tz6f4euj_ssang%20young%20pa%20background.png",
    seats: 5,
    transmission: "Automatic",
    fuel: "Petrol"
  },
  {
    id: 9,
    name: "Renault Trafic",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/45llik76_ranault%20trafic%20pa%20background.png",
    seats: "8+1",
    transmission: "Manual",
    fuel: "Diesel"
  },
  {
    id: 10,
    name: "Mercedes Sprinter",
    image: "https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/y5j68w5o_mercedes%20pa%20background%20.png",
    seats: "19+1",
    transmission: "Manual",
    fuel: "Diesel"
  }
];

// Features Data
const features = [
  {
    icon: Car,
    title: "Modern & Secure Fleet",
    description: "Drive with confidence in our latest models, maintained to the highest safety standards."
  },
  {
    icon: DollarSign,
    title: "Transparent Pricing",
    description: "What you see is what you pay—no hidden fees, no surprises."
  },
  {
    icon: Truck,
    title: "Convenient Delivery",
    description: "Your vehicle, delivered directly to your preferred location for maximum ease."
  },
  {
    icon: Clock,
    title: "Effortless Rental Process",
    description: "A smooth and efficient experience designed to save you time."
  },
  {
    icon: Shield,
    title: "Comprehensive Insurance",
    description: "Every vehicle is fully insured, ensuring complete peace of mind on every journey."
  },
  {
    icon: Headphones,
    title: "24/7 Dedicated Support",
    description: "Our team is available around the clock to assist you whenever needed."
  }
];

// Requirements Data
const requirements = [
  {
    icon: IdCard,
    title: "Valid Identification",
    description: "A government-issued ID or passport."
  },
  {
    icon: FileCheck,
    title: "Driving License",
    description: "A valid driver's license held in accordance with local regulations."
  },
  {
    icon: CreditCard,
    title: "Credit Card",
    description: "Required for payment and security deposit."
  },
  {
    icon: Users,
    title: "Minimum Age Requirement",
    description: "Drivers must be at least 21 years old."
  }
];

// Locations
const locations = [
  "Tirana Downtown",
  "Tirana International Airport"
];

// Services Data
const services = [
  {
    icon: CalendarDays,
    title: "Daily Car Rental",
    description: "Experience effortless mobility with our premium selection of modern, meticulously maintained vehicles. Whether for business or leisure, we ensure a seamless rental experience defined by comfort, safety, and style."
  },
  {
    icon: CalendarRange,
    title: "Long-Term Rental Solutions",
    description: "Enjoy the freedom of extended mobility with tailored long-term rental options. Designed for both individuals and corporate clients, our flexible plans adapt perfectly to your evolving needs."
  },
  {
    icon: FileText,
    title: "Operational Leasing",
    description: "Discover a fully integrated leasing solution where every detail is expertly managed. From documentation and maintenance to repairs and replacement vehicles, we provide a complete, worry-free driving experience."
  },
  {
    icon: Building2,
    title: "Fleet Management",
    description: "Empower your business with a professionally managed fleet. We deliver efficiency, reliability, and full operational support—ensuring your vehicles are always ready to perform at the highest standard."
  },
  {
    icon: UserCheck,
    title: "Chauffeur Service",
    description: "Travel with distinction through our professional chauffeur service. Our highly trained, multilingual drivers provide discretion, punctuality, and exceptional comfort for every journey."
  },
  {
    icon: Plane,
    title: "Private Transfer",
    description: "Indulge in a refined travel experience with our private transfer service. Whether within Albania or beyond, enjoy smooth, secure, and sophisticated transportation tailored to your schedule."
  }
];

// Navbar Component
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#why-us", label: "Why Us" },
    { href: "#services", label: "Our Services" },
    { href: "#vehicles", label: "Our Vehicles" },
    { href: "#contact", label: "Contact Us" }
  ];

  return (
    <nav className={`navbar fixed top-0 left-0 right-0 z-50 px-6 md:px-12 lg:px-24 py-4 ${scrolled ? 'scrolled' : 'bg-transparent'}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3" data-testid="navbar-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_rentigo-rides/artifacts/o2g0vt1s_logo%20e%20re.jpeg" 
            alt="Rentigo Car Rental" 
            className="h-10 w-auto"
          />
          <span className={`text-xl font-bold tracking-tight ${scrolled ? 'text-slate-900' : 'text-white'}`}>Rentigo</span>
        </a>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a 
              key={link.href}
              href={link.href} 
              className={`font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-white/90 hover:text-white'}`}
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <a href="#vehicles" className="btn-primary hidden md:block" data-testid="nav-book-now">
            Book Now
          </a>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            className={`md:hidden p-2 rounded-lg ${scrolled ? 'text-slate-900' : 'text-white'}`}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {menuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-slate-100" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-2">
            {navLinks.map((link) => (
              <a 
                key={link.href}
                href={link.href} 
                onClick={() => setMenuOpen(false)}
                className="block py-3 px-4 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a 
              href="#vehicles" 
              onClick={() => setMenuOpen(false)}
              className="block py-3 px-4 bg-orange-500 text-white text-center rounded-lg font-semibold mt-4"
            >
              Book Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="hero-section min-h-screen flex items-center justify-center relative" data-testid="hero-section">
      <div className="relative z-10 text-center px-6 md:px-12">
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 drop-shadow-lg" data-testid="hero-title">
          Your Ride, Your Adventure
        </h1>
        <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl mx-auto font-light" data-testid="hero-subtitle">
          Discover Albania with premium vehicles. Experience freedom on the road with Rentigo.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="#vehicles" className="btn-primary inline-block text-lg" data-testid="hero-cta">
            Explore Our Fleet
          </a>
          <a 
            href="https://www.instagram.com/rentigorentalcar.albania" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold py-3 px-6 rounded-full transition-all duration-300"
            data-testid="hero-instagram"
          >
            <Instagram className="w-5 h-5" />
            Follow us on Instagram
          </a>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUs = () => {
  return (
    <section id="why-us" className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-white" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-sky-500 font-semibold text-sm tracking-wider uppercase">Why Choose Us</span>
          <h2 className="section-title mt-4" data-testid="why-us-title">
            Exceptional Standards, Trusted Service
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="feature-card" data-testid={`feature-card-${index}`}>
              <feature.icon className="feature-icon w-12 h-12 text-sky-500 mb-6" />
              <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
              <p className="text-slate-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Our Services Section
const OurServices = () => {
  const servicesData = [
    {
      title: "Daily Car Rental",
      description: "Experience effortless mobility with our premium selection of modern, meticulously maintained vehicles. Whether for business or leisure, we ensure a seamless rental experience defined by comfort, safety, and style."
    },
    {
      title: "Long-Term Rental Solutions",
      description: "Enjoy the freedom of extended mobility with tailored long-term rental options. Designed for both individuals and corporate clients, our flexible plans adapt perfectly to your evolving needs."
    },
    {
      title: "Operational Leasing",
      description: "Discover a fully integrated leasing solution where every detail is expertly managed. From documentation and maintenance to repairs and replacement vehicles, we provide a complete, worry-free driving experience."
    },
    {
      title: "Fleet Management",
      description: "Empower your business with a professionally managed fleet. We deliver efficiency, reliability, and full operational support—ensuring your vehicles are always ready to perform at the highest standard."
    },
    {
      title: "Chauffeur Service",
      description: "Travel with distinction through our professional chauffeur service. Our highly trained, multilingual drivers provide discretion, punctuality, and exceptional comfort for every journey."
    },
    {
      title: "Private Transfer",
      description: "Indulge in a refined travel experience with our private transfer service. Whether within Albania or beyond, enjoy smooth, secure, and sophisticated transportation tailored to your schedule."
    }
  ];

  return (
    <section id="services" className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-sky-500 font-semibold text-sm tracking-wider uppercase">Our Services</span>
          <h2 className="section-title mt-4" data-testid="services-title">
            Comprehensive Mobility Solutions
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service, index) => (
            <div 
              key={index} 
              className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-200 hover:border-sky-200"
              data-testid={`service-card-${index}`}
            >
              <h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Rental Requirements Section
const RentalRequirements = () => {
  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="requirements-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-sky-500 font-semibold text-sm tracking-wider uppercase">Rental Requirements</span>
            <h2 className="section-title mt-4 mb-6" data-testid="requirements-title">
              Everything You Need to Get Started
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              To ensure a smooth and efficient rental experience, please have the following ready when you pick up your vehicle.
            </p>
            
            <div className="space-y-6">
              {requirements.map((req, index) => (
                <div key={index} className="requirement-item flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm" data-testid={`requirement-item-${index}`}>
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <req.icon className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{req.title}</h4>
                    <p className="text-slate-600">{req.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative hidden lg:block">
            <div className="absolute -top-8 -right-8 w-72 h-72 bg-sky-100 rounded-full opacity-50"></div>
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-orange-100 rounded-full opacity-50"></div>
            <img 
              src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=800&q=80" 
              alt="Car keys handover" 
              className="relative z-10 rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Vehicle Card Component
const VehicleCard = ({ vehicle, onGetQuote }) => {
  return (
    <div className="vehicle-card bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden group" data-testid={`vehicle-card-${vehicle.id}`}>
      <div className="aspect-[4/3] overflow-hidden bg-white">
        <img 
          src={vehicle.image} 
          alt={vehicle.name}
          className="vehicle-image w-full h-full object-contain"
        />
      </div>
      <div className="p-6">
        <div className="mb-4">
          <h3 className="text-xl font-bold text-slate-900">{vehicle.name}</h3>
          <span className="text-xs text-slate-400 italic">or similar</span>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="flex items-center gap-2 text-slate-600">
            <Users className="w-4 h-4 text-sky-500" />
            <span className="text-sm">{vehicle.seats}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <Settings2 className="w-4 h-4 text-sky-500" />
            <span className="text-sm">{vehicle.transmission}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <Fuel className="w-4 h-4 text-sky-500" />
            <span className="text-sm">{vehicle.fuel}</span>
          </div>
        </div>
        <Button 
          onClick={() => onGetQuote(vehicle)}
          className="btn-quote w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl transition-all duration-300"
          data-testid={`get-quote-btn-${vehicle.id}`}
        >
          Get a Quote
        </Button>
      </div>
    </div>
  );
};

// Quote Modal Component
const QuoteModal = ({ isOpen, onClose, vehicle }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    pickupLocation: '',
    dropoffLocation: '',
    pickupDate: null,
    dropoffDate: null,
    pickupTime: '',
    dropoffTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Time options from 00:00 to 23:30
  const timeOptions = [
    "00:00", "00:30", "01:00", "01:30", "02:00", "02:30",
    "03:00", "03:30", "04:00", "04:30", "05:00", "05:30",
    "06:00", "06:30", "07:00", "07:30", "08:00", "08:30",
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
    "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
    "18:00", "18:30", "19:00", "19:30", "20:00", "20:30",
    "21:00", "21:30", "22:00", "22:30", "23:00", "23:30"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pickupLocation || !formData.dropoffLocation || !formData.pickupDate || !formData.dropoffDate || !formData.pickupTime || !formData.dropoffTime) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quote`, {
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        vehicle_name: vehicle.name,
        pickup_location: formData.pickupLocation,
        dropoff_location: formData.dropoffLocation,
        pickup_date: formData.pickupDate ? `${formData.pickupDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${formData.pickupTime}` : '',
        dropoff_date: formData.dropoffDate ? `${formData.dropoffDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${formData.dropoffTime}` : '',
        message: formData.message
      });

      toast.success(response.data.message);
      onClose();
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        pickupLocation: '',
        dropoffLocation: '',
        pickupDate: null,
        dropoffDate: null,
        pickupTime: '',
        dropoffTime: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error("Failed to submit quote. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!vehicle) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="quote-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900" data-testid="quote-modal-title">
            Get a Quote for {vehicle.name}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Vehicle Info */}
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
            <img src={vehicle.image} alt={vehicle.name} className="w-24 h-18 object-cover rounded-lg" />
            <div>
              <h4 className="font-semibold text-slate-900">{vehicle.name}</h4>
              <p className="text-sm text-slate-600">{vehicle.seats} seats | {vehicle.transmission} | {vehicle.fuel}</p>
            </div>
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input 
                id="name"
                placeholder="John Doe"
                value={formData.customerName}
                onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                required
                data-testid="input-customer-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input 
                id="email"
                type="email"
                placeholder="john@example.com"
                value={formData.customerEmail}
                onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
                required
                data-testid="input-customer-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <Input 
              id="phone"
              type="tel"
              placeholder="+355 69 xxx xxxx"
              value={formData.customerPhone}
              onChange={(e) => setFormData({...formData, customerPhone: e.target.value})}
              required
              data-testid="input-customer-phone"
            />
          </div>

          {/* Location Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pick-up Location *</Label>
              <Select 
                value={formData.pickupLocation}
                onValueChange={(value) => setFormData({...formData, pickupLocation: value})}
              >
                <SelectTrigger data-testid="select-pickup-location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Drop-off Location *</Label>
              <Select 
                value={formData.dropoffLocation}
                onValueChange={(value) => setFormData({...formData, dropoffLocation: value})}
              >
                <SelectTrigger data-testid="select-dropoff-location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pick-up Date *</Label>
              <Input 
                type="date"
                value={formData.pickupDate ? formData.pickupDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setFormData({...formData, pickupDate: e.target.value ? new Date(e.target.value) : null})}
                min={new Date().toISOString().split('T')[0]}
                className="w-full"
                required
                data-testid="select-pickup-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Drop-off Date *</Label>
              <Input 
                type="date"
                value={formData.dropoffDate ? formData.dropoffDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setFormData({...formData, dropoffDate: e.target.value ? new Date(e.target.value) : null})}
                min={formData.pickupDate ? formData.pickupDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                className="w-full"
                required
                data-testid="select-dropoff-date"
              />
            </div>
          </div>

          {/* Time Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pick-up Time *</Label>
              <Select 
                value={formData.pickupTime}
                onValueChange={(value) => setFormData({...formData, pickupTime: value})}
              >
                <SelectTrigger data-testid="select-pickup-time">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeOptions.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Drop-off Time *</Label>
              <Select 
                value={formData.dropoffTime}
                onValueChange={(value) => setFormData({...formData, dropoffTime: value})}
              >
                <SelectTrigger data-testid="select-dropoff-time">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeOptions.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Additional Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Additional Message (Optional)</Label>
            <Textarea 
              id="message"
              placeholder="Any special requests or questions..."
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              rows={3}
              data-testid="input-message"
            />
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 rounded-xl"
            disabled={isSubmitting}
            data-testid="submit-quote-btn"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <div className="spinner"></div>
                Submitting...
              </span>
            ) : (
              'Submit Request'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Vehicles Section
const VehiclesSection = () => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleGetQuote = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsModalOpen(true);
  };

  return (
    <section id="vehicles" className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-white" data-testid="vehicles-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-sky-500 font-semibold text-sm tracking-wider uppercase">Our Fleet</span>
          <h2 className="section-title mt-4" data-testid="vehicles-title">
            Our Vehicles
          </h2>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto">
            Choose from our diverse fleet of well-maintained vehicles, perfect for any journey in Albania.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vehicles.map((vehicle) => (
            <VehicleCard 
              key={vehicle.id} 
              vehicle={vehicle} 
              onGetQuote={handleGetQuote}
            />
          ))}
        </div>
      </div>

      <QuoteModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        vehicle={selectedVehicle}
      />
    </section>
  );
};

// Contact Us Section
const ContactUs = () => {
  return (
    <section id="contact" className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-sky-500 font-semibold text-sm tracking-wider uppercase">Contact Us</span>
          <h2 className="section-title mt-4" data-testid="contact-title">
            Get In Touch
          </h2>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto">
            Have questions or ready to book? We're here to help you every step of the way.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
          {/* Phone */}
          <a 
            href="tel:+355696094224" 
            className="contact-card bg-white p-5 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 text-center group overflow-hidden"
            data-testid="contact-phone"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-sky-100 to-sky-50 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
              <Phone className="w-6 h-6 text-sky-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1">Phone</h3>
            <p className="text-sky-600 font-semibold text-xs break-all">+355 69 609 4224</p>
          </a>
          
          {/* WhatsApp */}
          <a 
            href="https://wa.me/355696094224" 
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card bg-white p-5 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 text-center group overflow-hidden"
            data-testid="contact-whatsapp"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
              <MessageCircle className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1">WhatsApp</h3>
            <p className="text-green-600 font-semibold text-xs break-all">+355 69 609 4224</p>
          </a>
          
          {/* Email */}
          <a 
            href="mailto:info@rentigorentalcar.com" 
            className="contact-card bg-white p-5 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 text-center group overflow-hidden"
            data-testid="contact-email"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
              <Mail className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1">Email</h3>
            <p className="text-orange-600 font-semibold text-xs break-all">info@rentigorentalcar.com</p>
          </a>
          
          {/* Website */}
          <a 
            href="https://www.rentigorentalcar.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="contact-card bg-white p-5 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 text-center group overflow-hidden"
            data-testid="contact-website"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
              <Globe className="w-6 h-6 text-slate-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1">Website</h3>
            <p className="text-slate-600 font-semibold text-xs break-all">rentigorentalcar.com</p>
          </a>
          
          {/* Instagram */}
          <a 
            href="https://www.instagram.com/rentigorentalcar.albania" 
            target="_blank" 
            rel="noopener noreferrer"
            className="contact-card bg-white p-5 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 text-center group overflow-hidden"
            data-testid="contact-instagram"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-purple-50 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
              <Instagram className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1">Instagram</h3>
            <p className="text-pink-600 font-semibold text-xs break-all">rentigorentalcar.albania</p>
          </a>
        </div>
        
        {/* Location */}
        <div className="mt-16 bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-center gap-3 mb-6">
            <MapPin className="w-6 h-6 text-sky-500" />
            <h3 className="text-xl font-bold text-slate-900">Our Locations</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center">
              <h4 className="font-semibold text-slate-900 mb-2">Tirana Downtown</h4>
              <p className="text-slate-600">Rruga Ndrek Luca Nr 4</p>
              <p className="text-slate-600">Tirana 1019</p>
            </div>
            <div className="text-center">
              <h4 className="font-semibold text-slate-900 mb-2">Tirana Airport</h4>
              <p className="text-slate-600">New Airport Road</p>
              <p className="text-slate-600">Next to Tirana Airport Hotel, Rinas, Tirana</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-16 px-6 md:px-12 lg:px-24" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Car className="h-8 w-8 text-sky-400" />
              <span className="text-2xl font-bold">Rentigo</span>
            </div>
            <p className="text-slate-400 leading-relaxed">
              Your trusted car rental partner in Albania. Experience freedom on the road with our premium fleet.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="#home" className="text-slate-400 hover:text-sky-400 transition-colors">Home</a></li>
              <li><a href="#why-us" className="text-slate-400 hover:text-sky-400 transition-colors">Why Choose Us</a></li>
              <li><a href="#services" className="text-slate-400 hover:text-sky-400 transition-colors">Our Services</a></li>
              <li><a href="#vehicles" className="text-slate-400 hover:text-sky-400 transition-colors">Our Vehicles</a></li>
              <li><a href="#contact" className="text-slate-400 hover:text-sky-400 transition-colors">Contact Us</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-sky-400" />
                <a href="tel:+355696094224" className="text-slate-400 hover:text-sky-400 transition-colors">
                  +355 69 609 4224
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-green-400" />
                <a href="https://wa.me/355696094224" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors">
                  WhatsApp
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-sky-400" />
                <a href="mailto:info@rentigorentalcar.com" className="text-slate-400 hover:text-sky-400 transition-colors">
                  info@rentigorentalcar.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-sky-400" />
                <a href="https://www.rentigorentalcar.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-sky-400 transition-colors">
                  www.rentigorentalcar.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram className="w-5 h-5 text-pink-400" />
                <a href="https://www.instagram.com/rentigorentalcar.albania" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-pink-400 transition-colors">
                  rentigorentalcar.albania
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-sky-400" />
                <span className="text-slate-400">Tirana, Albania</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-slate-800 mt-12 pt-8 text-center">
          <p className="text-slate-400" data-testid="footer-copyright">
            © {new Date().getFullYear()} Rentigo Car Rental. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Dental Clinic Advertisement Card
const DentalAdCard = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (isDismissed) return;
      
      // Get the document height and current scroll position
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show card when user is near the bottom (last 600px of the page)
      const isNearBottom = (scrollTop + windowHeight) >= (documentHeight - 600);
      setIsVisible(isNearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDismissed]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };

  if (!isVisible || isDismissed) return null;

  return (
    <div 
      className="fixed bottom-3 right-3 z-50 bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100"
      style={{ 
        boxShadow: '0 4px 15px rgba(0,0,0,0.12)',
        width: '170px',
      }}
      data-testid="dental-ad-card"
    >
      {/* Close Button */}
      <button
        onClick={handleDismiss}
        className="absolute top-1 right-1 z-10 bg-white/90 hover:bg-gray-100 rounded-full p-0.5 transition-colors"
        aria-label="Close advertisement"
        data-testid="dental-ad-close-btn"
      >
        <X className="w-3 h-3 text-gray-500" />
      </button>

      {/* Card Content */}
      <div className="p-2">
        {/* QR Code Image - Main focus */}
        <img 
          src="https://customer-assets-rejwkqb3.emergentagent.net/job_rentigo-rides/artifacts/exdp8rsh_photo%20dental%20.jpg"
          alt="TheraCare Albania"
          className="w-full h-auto object-contain rounded mb-2"
          data-testid="dental-ad-qr-code"
        />

        {/* Action Buttons */}
        <div className="flex flex-col gap-1.5">
          <a
            href="https://www.theracarealbania.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-white text-[10px] font-medium transition-all hover:opacity-90"
            style={{ backgroundColor: '#4A9B9B' }}
            data-testid="dental-ad-website-btn"
          >
            <Globe className="w-3 h-3" />
            Visit Website
          </a>
          <a
            href="mailto:info@theracarealbania.com"
            className="flex items-center justify-center gap-1.5 px-2 py-1 rounded text-[10px] font-medium transition-all hover:bg-gray-100 border border-gray-200"
            style={{ color: '#2D5A5A' }}
            data-testid="dental-ad-email-btn"
          >
            <Mail className="w-3 h-3" />
            Email Us
          </a>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Navbar />
      <HeroSection />
      <WhyChooseUs />
      <OurServices />
      <RentalRequirements />
      <VehiclesSection />
      <ContactUs />
      <Footer />
      <DentalAdCard />
    </div>
  );
}

export default App;
